import API from 'lib/tessitura/API';
import { ERROR_CONSTANTS, MODES } from 'constants';
import { partition, uniq, isObject, reject, get, find, has } from 'lodash';
import combineSubLineItems from 'lib/cart/combineSubLineItems';
import Config from 'config';

function normalizeErrorCode(errorMessage, fallbackMessage) {
  if (
    errorMessage.indexOf(
      'One or more of the selected seats is not available'
    ) !== -1
  ) {
    return ERROR_CONSTANTS.SEATS_UNAVAILABLE;
  }

  if (
    errorMessage.indexOf('This request will exceed the ticket limit') !== -1
  ) {
    return 'You have exceeded the ticket limit, please reduce your order to proceed.';
  }

  if (
    errorMessage.indexOf('This seat combination would leave single seats') !==
    -1
  ) {
    return (
      '<h3>Oops!</h3>One seat has been stranded at the end of a row or stuck between selected seats.' +
      ' Please leave at least two empty seats together.'
    );
  }

  return fallbackMessage;
}

export function getCartLineItems(cart) {
  let lineSeqNumbers = cart.map(x => x.li_seq_no);
  return uniq(lineSeqNumbers);
}

export function filterRemainingItems(cart, newSeats) {
  let filteredByNewSeats = cart.filter(item => {
    let newSeatIds = newSeats.map(x => x.seatData.seat_no);
    return newSeatIds.indexOf(parseInt(item.seat_no, 10)) !== -1;
  });

  return uniq(filteredByNewSeats).map(item => item.li_seq_no);
}

export function partitionExistingItems(lineItems, cart) {
  return partition(lineItems, item => {
    let cartIds = cart.map(x => parseInt(x.seat_no, 10));
    return cartIds.indexOf(item.seatData.seat_no) === -1;
  });
}

function releaseAll(perfNo, items, currentFixedPackage) {
  return Promise.all(
    items.map(function(item) {
      return API.ReleaseTickets(perfNo, item, currentFixedPackage);
    })
  );
}

function processNewFixedPackage(packageNo, lineItems) {
  let messages = {
    error: 'There was a problem adding your package.'
  };

  return new Promise((resolve, reject) => {
    API.AddPackageItemSeated(
      {
        pkg_no: packageNo,
        zone_no: 0
      },
      lineItems
    )
      .then(response => {
        let result;
        if (isObject(response.result)) {
          result = parseInt(response.result[0], 10);
        } else {
          result = parseInt(response.result, 10);
        }

        if (result === lineItems.length) {
          resolve(lineItems);
        } else {
          reject(new Error(messages.error));
        }
      })
      .catch(() => {
        reject(new Error(messages.error));
      });
  });
}

/**
 * Fixed package
 */
function processExistingFixedPackage(
  currentFixedPackage,
  lineItems,
  existingSeats
) {
  let messages = {
    partialError: 'There was a problem amending your seats.',
    removalError: 'There was an error, please try again.'
  };

  return new Promise((resolve, reject) => {
    API.RemovePackageItem(
      currentFixedPackage.pkg_no,
      currentFixedPackage.li_seq_no
    )
      .then(() => {
        API.AddPackageItemSeated(currentFixedPackage, lineItems)
          .then(response => {
            let result;
            if (isObject(response.result)) {
              result = parseInt(response.result[0], 10);
            } else {
              result = parseInt(response.result, 10);
            }

            /**
             * Failed to reserve all of your seats
             * but not a tessitura error.
             */
            if (result === 0) {
              API.AddPackageItemSeated(currentFixedPackage, existingSeats).then(
                resolve
              );
            }

            /**
             * Added all seats
             * or, added some but not others.
             */
            if (result === lineItems.length) {
              resolve(lineItems);
            } else {
              reject(new Error(messages.partialError));
            }
          })
          .catch(() => {
            reject(new Error(messages.partialError));
          });
      })
      .catch(() => {
        reject(new Error(messages.removalError));
      });
  });
}

/**
 * Single Seats
 */
function processAmendSingleSeats(perfNo, lineItems, cartItems, currentFixedPackage) {
  let [ newSeats, existingSeats ] = partitionExistingItems(lineItems, cartItems);
  let currentLineItems = getCartLineItems(cartItems);

  let messages = {
    reserveError:
      'Oops! \n One seat has been stranded at the end of a row or stuck between selected seats.' +
      ' Please leave at least two empty seats together.'
  };

  if (newSeats.length) {
    return new Promise(function(resolve, reject) {
      /**
       * Reserve new seats
       */
      API.ReserveTicketsSpecifiedSeats(perfNo, newSeats, currentFixedPackage).then(response => {
        const result = response.result.SeatsReserved;

        if (result === 0) {
          let newErr = new Error('Seats not added');
          newErr.response = response.error;
          reject(newErr);
        }

        if (result === newSeats.length) {
          /**
           * Release old line items
           */
          releaseAll(perfNo, currentLineItems, currentFixedPackage).then(() => {
            /**
             * If we have existing seats from our original line item re-add them
             */
            if (existingSeats.length) {
              API.ReserveTicketsSpecifiedSeats(perfNo, existingSeats).then(reserveResponse => {
                resolve(reserveResponse);
              }).catch(() => {
                reject(new Error(messages.reserveError));
              });
            } else {
              resolve(response);
            }
          }).catch(() => {
            reject(new Error(messages.reserveError));
          });
        } else {
          API.getCart().then(newCart => {
            const newItems = combineSubLineItems(newCart, perfNo);

            const remainingLineItems = filterRemainingItems(newItems, newSeats);

            releaseAll(perfNo, remainingLineItems, currentFixedPackage).then(releaseResponse => {
              resolve(releaseResponse);
            }).catch(() => {
              reject(new Error(messages.reserveError));
            });
          })
          .catch(() => {
            reject(new Error(messages.reserveError));
          });
        }
      }, errResponse => {
        reject(new Error(normalizeErrorCode(errResponse.message, messages.reserveError)));
      });
    });
  } else {
    return new Promise(function(resolve, reject) {
      releaseAll(perfNo, currentLineItems, currentFixedPackage).then(() => {
        API.ReserveTicketsSpecifiedSeats(perfNo, existingSeats).then(reserveResponse => {
          resolve(reserveResponse);
        }, () => {
          reject(new Error(messages.reserveError));
        });
      }, () => {
        reject(new Error(messages.reserveError));
      });
    });
  }
}

function processNewSeats(perfNo, selections) {
  return API.ReserveTicketsSpecifiedSeats(perfNo, selections).then(successResponse => {
    return successResponse;
  }, errResponse => {
    return Promise.reject(new Error(normalizeErrorCode(errResponse.message, 'There was a problem reserving your seats.')));
  }).catch(() => {
    let config = new Config().getConfig();

    if (has(config, 'customMessages')) {
      const failed = find(config.customMessages, 'failed').failed

      if (failed) {
        return Promise.reject(new Error(failed));
      }

      return Promise.reject(new Error('Failed to reserve some seats'));
    }

    return Promise.reject(new Error('Failed to reserve some seats'));
  });
}

function processNewFlexPackage(flexPackage, packageNo, performanceNo, performanceGroupNo, priceTypes, additionalPriceTypes, lineItems) {
  const messages = {
    noPackageSeats: 'You must select package seats.',
    noSeatsAddedError: 'There was a problem reserving your seats.',
    someSeatsAddedError: 'Some seats could not be added, please amend your seats and try again.',
    error: 'There was an error, please try again.',
  };

  // Extract package line items
  const packageLineItems = lineItems.filter(item => {
    return priceTypes.indexOf(item.priceData.price_type) !== -1;
  });

  // No need to go any further without any line items
  if (!packageLineItems.length) {
    return Promise.reject(new Error(messages.noPackageSeats));
  }

  // Extract performance line items
  const performanceLineItems = lineItems.filter(item => {
    return additionalPriceTypes.indexOf(item.priceData.price_type) !== -1;
  });

  // Line item should be from parent if package already exists
  const parentLineItem = flexPackage ? flexPackage.li_seq_no : 0;

  // Try to add the seats
  return API.AddNFSPackagePerformanceItem(parentLineItem, packageNo, performanceGroupNo, performanceNo, packageLineItems).then(response => {
    const { LineItemId, NFSPackageLineItemId, SeatsReserved } = response.result;

    // If nothing got added there is nothing more to do, die!
    if (SeatsReserved === 0) {
      return Promise.reject(new Error(messages.noSeatsAddedError));
    }

    // In this case, seats were added, but not all of them
    // so we have to drop any of the seats that were added and die!
    if (SeatsReserved !== packageLineItems.length) {
      return API.RemoveNFSPackage(NFSPackageLineItemId, LineItemId, performanceNo).then(() => {
        return Promise.reject(new Error(messages.someSeatsAddedError));
      });
    }

    // We reserved the requested number of seats
    return [ NFSPackageLineItemId, LineItemId ];
  }).then(packageResponse => {
    // No performance line items?
    if (!performanceLineItems.length) {
      return packageLineItems;
    }

    // Grab added package details
    const [ NFSPackageLineItemId, PackagePerfLineItemId ] = packageResponse;

    // Attempt to reserve the seats
    return API.ReserveTicketsSpecifiedSeats(performanceNo, performanceLineItems).then(response => {
      const { LineItemId, SeatsReserved } = response.result;

      // No performance seats were reserved, remove entire package
      if (SeatsReserved === 0) {
        return API.RemoveNFSPackage(NFSPackageLineItemId, PackagePerfLineItemId, performanceNo).then(() => {
          return Promise.reject(new Error(messages.noSeatsAddedError));
        });
      }

      // Some performance seats added, but not all, so remove the performance
      // and remove the package
      if (SeatsReserved !== performanceLineItems.length) {
        return Promise.all([
          API.RemoveNFSPackage(NFSPackageLineItemId, PackagePerfLineItemId, performanceNo),
          API.ReleaseTickets(performanceNo, LineItemId),
        ]).then(() => {
          return Promise.reject(new Error(messages.someSeatsAddedError));
        });
      }

      // We reserved the requested number of seats
      return lineItems;
    });
  });
}

function flexPackageItemsToRemove(packageNo, performanceNo, cartItems, priceTypes) {
  let lineItems = cartItems.filter(item => {
    return (
      priceTypes.indexOf(item.price_type) !== -1 &&
      item.pkg_no === packageNo &&
      item.perf_no === performanceNo
    );
  });

  lineItems = lineItems.map(item => {
    return item.li_seq_no;
  });

  return uniq(lineItems);
}

function additionalItemsToRemove(performanceNo, cartItems, priceTypes) {
  let lineItems = cartItems.filter(item => {
    return (
      priceTypes.indexOf(item.price_type) !== -1 &&
      item.perf_no === performanceNo
    );
  });

  lineItems = lineItems.map(item => {
    return item.li_seq_no;
  });

  return uniq(lineItems);
}

function processExistingFlexPackage(flexPackage, packageNo, performanceNo, performanceGroupNo, priceTypes, additionalPriceTypes, lineItems, cartItems) {
  const messages = {
    noSeatsAddedError: 'There was a problem reserving your additional seats.',
    someSeatsAddedError: 'Some additional seats could not be added, please amend your seats and try again.',
    noPackageSeatsAddedError: 'There was a problem reserving your package seats.',
    somePackageSeatsAddedError: 'Some package seats could not be added, please amend your seats and try again.',
    error: 'There was an error, please try again.',
  };

  // Extract information from flex package
  const { pkg_li_no } = flexPackage;

  // Find package seats from our selected seats
  const packageLineItems = lineItems.filter(item => {
    return priceTypes.indexOf(item.priceData.price_type) !== -1;
  });

  // Has there been any changes to package seats?
  const [ newPackageSeats, existingPackageSeats ] = partitionExistingItems(packageLineItems, cartItems);

  // Grab old package items we no longer need
  const currentPackageLineItems = flexPackageItemsToRemove(packageNo, performanceNo, cartItems, priceTypes);

  // Find additional seats from our selected seats
  const additionalLineItems = lineItems.filter(item => {
    return additionalPriceTypes.indexOf(item.priceData.price_type) !== -1;
  });

  // Has there been any changes to additional seats?
  const [ newAdditionalSeats, existingAdditionalSeats ] = partitionExistingItems(additionalLineItems, cartItems);

  // Grab old additional items we no longer need
  const currentAdditionalLineItems = additionalItemsToRemove(performanceNo, cartItems, additionalPriceTypes);

  let promise = Promise.resolve(true);

  // We only really need to do the package process if there are new seats
  // we sort of have to drop all items, then re-add them. Real pain in the ass
  // that you can't individually drop subline items, you have to drop the
  // entire performance
  if (newPackageSeats.length) {
    promise = promise.then(() => {
      const promises = currentPackageLineItems.map(li_seq_no => {
        return API.RemoveNFSPackage(pkg_li_no, li_seq_no, performanceNo);
      });

      return Promise.all(promises);
    }).then(() => {
      // We sort of have to do this, there's no way around it. We need to
      // check if the package performance we removed also nuked the entire
      // item in case it was the only package
      return API.getCart();
    }).then(response => {
      let packageLineItem = 0;

      if (get(response, 'PackageLineItem')) {
        const parentPackage = get(response, 'PackageLineItem').find(item => {
          return item.pkg_no === packageNo && item.perf_no === null;
        });

        if (parentPackage) {
          packageLineItem = parentPackage.pkg_li_no;
        }
      }

      // Now the cart has been voided of the old items, we need to add
      // the new item to the cart, hell add the old ones, too.
      return API.AddNFSPackagePerformanceItem(packageLineItem, packageNo, performanceGroupNo, performanceNo, packageLineItems);
    }).then(response => {
      const { NFSPackageLineItemId, LineItemId, SeatsReserved } = response.result;

      // If nothing got added there is nothing more to do, die!
      if (SeatsReserved === 0) {
        return Promise.reject(new Error(messages.noPackageSeatsAddedError));
      }

      // In this case, seats were added, but not all of them
      // so we have to drop any of the seats that were added and die!
      if (SeatsReserved !== packageLineItems.length) {
        return API.RemoveNFSPackage(NFSPackageLineItemId, LineItemId, performanceNo).then(() => {
          return Promise.reject(new Error(messages.somePackageSeatsAddedError));
        });
      }

      // Success
      return LineItemId;
    });
  }

  // Single tickets work.. normally, so lets try to add the seat first and
  // only then we remove the old seats. I hate Tessitura inconsistencies.
  if (newAdditionalSeats.length) {
    promise = promise.then(() => {
      return API.ReserveTicketsSpecifiedSeats(performanceNo, newAdditionalSeats);
    }).then(response => {
      const { LineItemId, SeatsReserved } = response.result;

      // No performance seats were reserved
      if (SeatsReserved === 0) {
        return Promise.reject(new Error(messages.noSeatsAddedError));
      }

      // Some performance seats added, but not all, so remove the performance
      // and remove the new package performance item
      if (SeatsReserved !== newAdditionalSeats.length) {
        return API.ReleaseTickets(performanceNo, LineItemId).then(() => {
          return Promise.reject(new Error(messages.someSeatsAddedError));
        });
      }

      // Success
      return LineItemId;
    }).then(() => {
      // Remove the shit out of the remainder of performance items
      const promises = currentAdditionalLineItems.map(li_seq_no => {
        return API.ReleaseTickets(performanceNo, li_seq_no);
      });

      return Promise.all(promises);
    }).then(() => {
      // Re-add any straggler seats
      if (!existingAdditionalSeats.length) {
        return true;
      }

      // We don't need to error check, we literally dropped those seats so
      // they should be available to us this quickly
      return API.ReserveTicketsSpecifiedSeats(performanceNo, existingAdditionalSeats);
    });
  }

  return promise.then(() => {
    return lineItems;
  });
}

/**
 * Process Mini Cart
 */
export default function processMiniCart(params) {
  const {
    mode,
    cartItems,
    lineItems,
    performanceNo,
    packageNo,
    currentFixedPackage,
    currentFlexPackage,
    currentFlexPackagePerformance,
    performanceGroupNo,
    priceTypes,
    additionalPriceTypes,
  } = params;

  /**
   * If we don't have an existing cart then this is a
   * new session and we can just reserve tickets.
   */
  if (mode === MODES.FLEX_PACKAGE_PERFORMANCE && !currentFlexPackage) {
    /**
     * If we are choosing seats for a new NFS package
     */
    return processNewFlexPackage(
      currentFlexPackage, packageNo, performanceNo, performanceGroupNo, priceTypes, additionalPriceTypes, lineItems
    );
  } else if (mode === MODES.FIXED_PACKAGE && !currentFixedPackage) {
    /**
     * If we are choosing seats for a new package
     */
    return processNewFixedPackage(packageNo, lineItems);
  } else if (mode === MODES.PERFORMANCE && !cartItems.length) {
    /**
     * If we are choosing seats for a new performance
     */
    return processNewSeats(performanceNo, lineItems);
  }

  /**
   * If we are editing a fixed package performance
   */
  // if (currentFixedPackage) {
  //   return processExistingFixedPackage(currentFixedPackage, lineItems);
  // }

  if (mode === MODES.FLEX_PACKAGE_PERFORMANCE) {
    return processExistingFlexPackage(
      currentFlexPackage, packageNo, performanceNo, performanceGroupNo, priceTypes, additionalPriceTypes, lineItems, cartItems
    );
  }

  /**
   * If we are editing single seats
   *
   * Note: Editing fixed package seats at the performance level
   *       is included here as package seats are locked and
   *       excluded from processing which lets us treat add on
   *       seats simply like single seats.
   *
   */
  return processAmendSingleSeats(
    performanceNo,
    lineItems,
    cartItems,
    packageNo
  );
}



// WEBPACK FOOTER //
// ./src/javascripts/lib/cart/processMiniCart.js